import { userStorage } from '../lib/userStorage';

export const GENERAL_PROGRESS_CARDS_KEY = 'progress_general_cards';
export const GENERAL_PROGRESS_SCHEDULE_VERSION = 2;

const CARD_TEMPLATES = [
  {
    id: 'letters-rec',
    title: 'Letters of Recommendation',
    description: 'Ask early, give teachers/counselors context, and confirm everything is submitted.',
    items: [
      ['letters-1', 'Decide which teachers to ask', 5, 15, false, '1-week'],
      ['letters-2', 'Ask teachers and counselor early', 8, 25, false, '1-week'],
      ['letters-3', 'Send resume, brag sheet, or helpful context', 9, 1, false, '3-days'],
      ['letters-4', 'Confirm school forms and letters were submitted', 10, 20, false, '1-week'],
    ],
  },
  {
    id: 'main-essay',
    title: 'Main Essay',
    description: 'Get the first draft out early, then revise for growth, voice, and clarity.',
    items: [
      ['essay-1', 'Choose a personal essay direction', 6, 15, false, '1-week'],
      ['essay-2', 'Write a rough first draft', 7, 1, false, '1-week'],
      ['essay-3', 'Get feedback from someone who knows your voice', 7, 20, false, '3-days'],
      ['essay-4', 'Revise and complete final proofread', 8, 10, false, '1-week'],
    ],
  },
  {
    id: 'supplements',
    title: 'Supplemental Essays',
    description: 'Track repeated themes like why major, why us, community, activity, and identity.',
    items: [
      ['supp-1', 'List every supplemental prompt from your final colleges', 8, 15, false, '1-week'],
      ['supp-2', 'Group prompts that can reuse ideas', 8, 22, false, '3-days'],
      ['supp-3', 'Draft school-specific responses', 9, 20, false, '1-week'],
      ['supp-4', 'Check word counts and college names before submitting', 10, 20, false, '1-week'],
    ],
  },
  {
    id: 'fafsa-css',
    title: 'FAFSA & CSS Profile',
    description: 'Financial aid forms can have separate priority dates, so start as soon as they open.',
    items: [
      ['aid-1', 'Create or confirm FAFSA account access', 9, 15, false, '1-week'],
      ['aid-2', 'Complete FAFSA as soon as possible after it opens', 10, 15, false, '1-week'],
      ['aid-3', 'Check which schools require CSS Profile', 9, 20, false, '1-week'],
      ['aid-4', 'Confirm each college received required aid forms', 1, 15, true, '1-week'],
    ],
  },
  {
    id: 'testing',
    title: 'SAT / ACT Score Sends',
    description: 'Confirm each college’s test policy before paying to send official scores.',
    items: [
      ['test-1', 'Check test optional or test required policy for each school', 8, 20, false, '1-week'],
      ['test-2', 'Decide which SAT or ACT scores to send', 9, 15, false, '1-week'],
      ['test-3', 'Send official scores where required', 10, 15, false, '1-week'],
      ['test-4', 'Confirm portals show scores received when needed', 11, 10, false, '3-days'],
    ],
  },
  {
    id: 'after-submit',
    title: 'After Submitting',
    description: 'Submitted does not always mean complete. Portals often show missing items.',
    items: [
      ['post-1', 'Create or log into each applicant portal', 11, 5, false, '3-days'],
      ['post-2', 'Check for missing transcripts, recommendations, scores, or forms', 11, 10, false, '3-days'],
      ['post-3', 'Save portal login links in Link Hub or a dashboard', 11, 12, false, 'day-of'],
      ['post-4', 'Track decisions and financial aid updates', 3, 15, true, '1-week'],
    ],
  },
];

function localDateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function getApplicationCycleStartYear(graduationYear, today = new Date()) {
  const currentYear = today.getFullYear();
  const parsedGraduationYear = Number(graduationYear);
  if (!Number.isInteger(parsedGraduationYear) || parsedGraduationYear < 1900) return null;
  if (Number.isInteger(parsedGraduationYear) && parsedGraduationYear > currentYear) {
    return parsedGraduationYear - 1;
  }
  return today.getMonth() >= 5 ? currentYear : currentYear - 1;
}

export function createGeneralProgressCards(graduationYear, today = new Date()) {
  const cycleStartYear = getApplicationCycleStartYear(graduationYear, today);
  const todayKey = localDateKey(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const hasGraduationYear = cycleStartYear !== null;

  return CARD_TEMPLATES.map((card) => ({
    id: card.id,
    title: card.title,
    description: card.description,
    scheduleVersion: GENERAL_PROGRESS_SCHEDULE_VERSION,
    generatedForGraduationYear: hasGraduationYear ? Number(graduationYear) : null,
    cycleStartYear,
    items: card.items.map(([id, text, month, day, followingYear, reminderOffset]) => {
      const suggestedMonthDay = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const suggestedDate = hasGraduationYear
        ? localDateKey(cycleStartYear + (followingYear ? 1 : 0), month, day)
        : '';
      const isPast = Boolean(suggestedDate && suggestedDate < todayKey);
      return {
        id,
        text,
        complete: false,
        date: isPast ? '' : suggestedDate,
        reminderOffset: !suggestedDate || isPast ? null : reminderOffset,
        dateSource: 'suggested',
        suggestedMonthDay,
      };
    }),
  }));
}

function readJson(key, fallback) {
  try {
    const raw = userStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function isLegacySuggestedItem(item, templateItem) {
  if (item.dateSource === 'suggested') return true;
  if (item.dateSource === 'manual') return false;
  if (item.date === undefined) return true;
  return Boolean(item.date && item.date.slice(5) === templateItem.suggestedMonthDay);
}

export function reconcileGeneralProgressSchedule(today = new Date()) {
  const personalInfo = readJson('my_info_personal', {});
  const graduationYear = personalInfo.graduationYearConfirmed
    ? Number(personalInfo.graduationYear) || null
    : null;
  const generatedCards = createGeneralProgressCards(graduationYear, today);
  const storedCards = readJson(GENERAL_PROGRESS_CARDS_KEY, null);

  if (!Array.isArray(storedCards)) {
    userStorage.setItem(GENERAL_PROGRESS_CARDS_KEY, JSON.stringify(generatedCards));
    return generatedCards;
  }

  // An unconfirmed legacy default must never rewrite a student's existing schedule.
  if (!graduationYear) return storedCards;

  let changed = false;
  const reconciled = storedCards.map((card) => {
    const generatedCard = generatedCards.find((candidate) => candidate.id === card.id);
    if (!generatedCard) return card;

    const items = card.items.map((item) => {
      const generatedItem = generatedCard.items.find((candidate) => candidate.id === item.id);
      if (!generatedItem || !isLegacySuggestedItem(item, generatedItem)) return item;
      const nextItem = {
        ...item,
        date: generatedItem.date,
        reminderOffset: generatedItem.reminderOffset,
        dateSource: 'suggested',
        suggestedMonthDay: generatedItem.suggestedMonthDay,
      };
      if (JSON.stringify(nextItem) !== JSON.stringify(item)) changed = true;
      return nextItem;
    });

    const nextCard = {
      ...card,
      scheduleVersion: GENERAL_PROGRESS_SCHEDULE_VERSION,
      generatedForGraduationYear: graduationYear,
      cycleStartYear: generatedCard.cycleStartYear,
      items,
    };
    if (JSON.stringify(nextCard) !== JSON.stringify(card)) changed = true;
    return nextCard;
  });

  if (changed) userStorage.setItem(GENERAL_PROGRESS_CARDS_KEY, JSON.stringify(reconciled));
  return reconciled;
}
